import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import showToast from "utils/toast";
import V2AuthInput from "./components/V2AuthInput";
import V2AuthButton from "./components/V2AuthButton";

export default function V2Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get("next");
  const next = rawNext && rawNext.startsWith("/") ? rawNext : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [error, setError] = useState("");

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authToken = localStorage.getItem("authToken");
        const userStr = localStorage.getItem("user");
        const user = userStr ? JSON.parse(userStr) : null;

        if (authToken && user?.is_verified) {
          const pendingInvite = sessionStorage.getItem("pendingInviteUrl");
          if (pendingInvite) {
            sessionStorage.removeItem("pendingInviteUrl");
            navigate(pendingInvite, { replace: true });
          } else {
            navigate("/v2", { replace: true });
          }
          return;
        }
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const { data } = await betaAPI.login({
        body: { email, password },
      });

      // Store auth data
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      // Check if email is verified
      if (data.user.is_verified === false) {
        navigate("/v2/auth/verify-email-sent", { replace: true });
        return;
      }

      // Success - redirect
      showToast.success(
        t("auth.login.welcomeBack", { name: data.user?.fname || "" })
      );

      const pendingInvite = sessionStorage.getItem("pendingInviteUrl");
      if (next) {
        navigate(next, { replace: true });
      } else if (pendingInvite) {
        sessionStorage.removeItem("pendingInviteUrl");
        navigate(pendingInvite, { replace: true });
      } else {
        navigate("/v2", { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || t("auth.login.failed"));
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking auth
  if (checkingAuth) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#E07A3A]" />
        <p className="mt-4 text-sm text-[#6B7280]">
          {t("auth.login.checkingAuth")}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="mb-2 text-xl font-bold text-[#111827]">
          {t("auth.login.title")}
        </h1>
        <p className="text-sm text-[#6B7280]">{t("auth.login.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Email */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#374151]">
            {t("auth.common.email")}
          </label>
          <V2AuthInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("auth.common.emailPlaceholder")}
            error={!!error}
            disabled={isLoading}
            autoComplete="email"
          />
        </div>

        {/* Password */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#374151]">
            {t("auth.common.password")}
          </label>
          <V2AuthInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("auth.common.passwordPlaceholder")}
            error={!!error}
            disabled={isLoading}
            autoComplete="current-password"
          />
        </div>

        {/* Error message */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Forgot password */}
        <div className="text-right">
          <Link
            to="/v2/auth/forgot-password"
            className="text-sm text-[#E07A3A] transition-colors hover:text-[#D66B2E]"
          >
            {t("auth.login.forgotPassword")}
          </Link>
        </div>

        {/* Submit button */}
        <V2AuthButton
          type="submit"
          variant="primary"
          disabled={!email || !password}
          loading={isLoading}
        >
          {t("auth.login.submit")}
        </V2AuthButton>
      </form>

      {/* Register link */}
      <div className="mt-6 text-center">
        <p className="text-sm text-[#6B7280]">
          {t("auth.login.notRegistered")}{" "}
          <Link
            to="/v2/auth/register"
            className="font-medium text-[#E07A3A] transition-colors hover:text-[#D66B2E]"
          >
            {t("auth.login.createAccount")}
          </Link>
        </p>
      </div>
    </>
  );
}
